import * as THREE from 'three';
import type { Course } from '../course/Course';
import type { Hole } from '../course/Hole';
import { SurfaceType } from '../terrain/SurfaceType';
import type { AiSkillProfile } from './ai/AiGolfer';

export type CompetitorType = 'human' | 'ai';
export type CompetitionRoundStatus = 'playing' | 'hole-complete' | 'round-complete';

export interface Competitor {
  readonly id: string;
  readonly name: string;
  readonly type: CompetitorType;
  readonly skillProfile?: AiSkillProfile;
  ballPosition: THREE.Vector3;
  currentLie: SurfaceType;
  currentHoleStrokes: number;
  perHoleScores: Array<number | null>;
  totalScore: number;
  holed: boolean;
  active: boolean;
}

export interface LeaderboardEntry {
  readonly competitorId: string;
  readonly name: string;
  readonly totalScore: number;
  readonly relativeToPar: number;
  readonly holesCompleted: number;
}

export class CompetitionRound {
  readonly competitors: Competitor[];
  activeHoleNumber: number;
  activeTurnCompetitorId: string;
  roundState: CompetitionRoundStatus = 'playing';

  constructor(
    readonly course: Course,
    competitors: readonly Competitor[],
  ) {
    if (course.holes.length === 0) throw new Error('CompetitionRound requires at least one hole');
    if (competitors.length === 0) throw new Error('CompetitionRound requires competitors');
    this.competitors = competitors.map((competitor) => ({
      ...competitor,
      ballPosition: competitor.ballPosition.clone(),
      perHoleScores: [...competitor.perHoleScores],
    }));
    this.activeHoleNumber = course.holes[0].number;
    this.activeTurnCompetitorId = this.competitors[0].id;
  }

  get activeHole(): Hole {
    return this.course.getHole(this.activeHoleNumber) ?? this.course.holes[0];
  }

  get activeTurn(): Competitor {
    return this.competitor(this.activeTurnCompetitorId);
  }

  get leaderboardState(): LeaderboardEntry[] {
    const completedPar = this.completedPar();
    return this.competitors
      .map((competitor) => ({
        competitorId: competitor.id,
        name: competitor.name,
        totalScore: competitor.totalScore,
        relativeToPar: competitor.totalScore - completedPar,
        holesCompleted: competitor.perHoleScores.filter((score) => score !== null).length,
      }))
      .sort((a, b) => a.totalScore - b.totalScore);
  }

  competitor(id: string): Competitor {
    const competitor = this.competitors.find((entry) => entry.id === id);
    if (!competitor) throw new Error(`Unknown competitor: ${id}`);
    return competitor;
  }

  recordStroke(competitorId: string): void {
    if (this.roundState !== 'playing') return;
    const competitor = this.competitor(competitorId);
    competitor.currentHoleStrokes += 1;
  }

  updateBall(competitorId: string, position: THREE.Vector3, lie: SurfaceType): void {
    const competitor = this.competitor(competitorId);
    competitor.ballPosition.copy(position);
    competitor.currentLie = lie;
  }

  markHoled(competitorId: string): void {
    const competitor = this.competitor(competitorId);
    competitor.holed = true;
    const holeIndex = this.holeIndex();
    competitor.perHoleScores[holeIndex] = competitor.currentHoleStrokes;
    competitor.totalScore = this.completedStrokeTotal(competitor);
    this.chooseNextTurn();
    if (this.competitors.every((entry) => entry.holed || !entry.active)) {
      this.roundState = this.isFinalHole() ? 'round-complete' : 'hole-complete';
    }
  }

  chooseNextTurn(): void {
    if (this.roundState !== 'playing') return;
    const candidates = this.competitors.filter(
      (competitor) => competitor.active && !competitor.holed,
    );
    if (candidates.length === 0) return;
    const pin = this.activeHole.activePin.position;
    const next = candidates.reduce((farther, competitor) =>
      competitor.ballPosition.distanceToSquared(pin) > farther.ballPosition.distanceToSquared(pin)
        ? competitor
        : farther,
    );
    this.activeTurnCompetitorId = next.id;
  }

  advanceToNextHole(): number | null {
    if (this.roundState !== 'hole-complete') return null;
    const index = this.holeIndex();
    const next = this.course.holes[index + 1];
    if (!next) return null;
    this.activeHoleNumber = next.number;
    const tee = next.primaryTee.position;
    this.competitors.forEach((competitor) => {
      competitor.ballPosition.copy(tee);
      competitor.currentLie = SurfaceType.Tee;
      competitor.currentHoleStrokes = 0;
      competitor.holed = false;
      competitor.active = true;
    });
    this.roundState = 'playing';
    this.activeTurnCompetitorId = this.honorCompetitorId(index) ?? this.competitors[0].id;
    return next.number;
  }

  restartHole(): void {
    const tee = this.activeHole.primaryTee.position;
    this.competitors.forEach((competitor) => {
      competitor.ballPosition.copy(tee);
      competitor.currentLie = SurfaceType.Tee;
      competitor.currentHoleStrokes = 0;
      competitor.holed = false;
      competitor.active = true;
      competitor.perHoleScores[this.holeIndex()] = null;
    });
    this.roundState = 'playing';
    this.activeTurnCompetitorId =
      this.honorCompetitorId(this.holeIndex() - 1) ?? this.competitors[0].id;
  }

  restartRound(): void {
    const first = this.course.holes[0];
    this.activeHoleNumber = first.number;
    this.competitors.forEach((competitor) => {
      competitor.ballPosition.copy(first.primaryTee.position);
      competitor.currentLie = SurfaceType.Tee;
      competitor.currentHoleStrokes = 0;
      competitor.perHoleScores = first ? this.course.holes.map(() => null) : [];
      competitor.totalScore = 0;
      competitor.holed = false;
      competitor.active = true;
    });
    this.roundState = 'playing';
    this.activeTurnCompetitorId = this.competitors[0].id;
  }

  canHumanSwing(humanId = 'player'): boolean {
    return (
      this.roundState === 'playing' &&
      this.activeTurnCompetitorId === humanId &&
      !this.competitor(humanId).holed
    );
  }

  private honorCompetitorId(previousHoleIndex: number): string | null {
    let best: Competitor | null = null;
    for (const competitor of this.competitors) {
      const score = competitor.perHoleScores[previousHoleIndex];
      if (score === null) continue;
      if (!best || score < (best.perHoleScores[previousHoleIndex] ?? Number.POSITIVE_INFINITY)) {
        best = competitor;
      }
    }
    return best?.id ?? null;
  }

  private holeIndex(): number {
    return Math.max(
      0,
      this.course.holes.findIndex((hole) => hole.number === this.activeHoleNumber),
    );
  }

  private isFinalHole(): boolean {
    return this.holeIndex() === this.course.holes.length - 1;
  }

  private completedStrokeTotal(competitor: Competitor): number {
    return competitor.perHoleScores.reduce<number>((sum, score) => sum + (score ?? 0), 0);
  }

  private completedPar(): number {
    return this.course.holes.reduce((sum, hole, index) => {
      const completed = this.competitors.every(
        (competitor) => competitor.perHoleScores[index] !== null,
      );
      return sum + (completed ? hole.par : 0);
    }, 0);
  }
}
